/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { getAsset, getUrlSync } from '@msdyn365-commerce/core';
import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { format } from '@msdyn365-commerce/retail-proxy';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface IHeaderViewState {
    isAccountMobileMenuOpen: boolean;
    searchActive: boolean;
    customerSwitchUrl?: string;
}

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>, IHeaderViewState> {
    private readonly menuNode: React.RefObject<HTMLDivElement>;

    private readonly _accountInfoDropdownRef: React.RefObject<HTMLDivElement>;

    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {
        super(props);
        this.state = {
            isAccountMobileMenuOpen: false,
            searchActive: false
        };
        this.menuNode = React.createRef();
        this._accountInfoDropdownRef = React.createRef();
    }

    private _previousHeaderHeight = 0;

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            window.addEventListener('click', (event: MouseEvent) => {
                if (!this._accountInfoDropdownRef.current?.contains(event.target as Node)) {
                    this.setState({ isAccountMobileMenuOpen: false });
                }
            });

            const searchContainer = document.querySelector('.ms-search');

            searchContainer?.addEventListener('click', (e) => {
                if (!this.state.searchActive) {
                    // First click will "activate" the search bar, therefore we need to stop the event propagation
                    this.setState({ searchActive: true });
                    e.stopPropagation();
                }
            });

            searchContainer?.addEventListener('keyup', (e: Event) => {
                const event = e as KeyboardEvent;
                if (event.key === 'Escape') {
                    this.setState({ searchActive: false });
                }
            });

            // Detect click outside of search container
            document.addEventListener('click', (e) => {
                const scannerModal = document.querySelector('.vh-barcode-scanner__wrapper');
                const clickedInsideScanner = scannerModal?.classList.contains('show') && scannerModal?.contains(e.target as Node);

                if (!clickedInsideScanner && !searchContainer?.contains(e.target as Node)) {
                    this.setState({ searchActive: false });
                }
            });

            this.setState({ customerSwitchUrl: this._getCustomerSwitchUrl() });
        }

        this._monitorHeaderHeight();
    }

    private _getCustomerSwitchUrl = (): string | undefined => {
        const route = getUrlSync('businessPartnerSelection', this.props.context.actionContext);
        if (!route || !MsDyn365.isBrowser) {
            return undefined;
        }

        // MsDyn365.getUrlSync persists some params in the route and remove other params.
        // Among the persisted params, we need to remove catalogid.
        const persistedParamToRemove = ['catalogid'];
        const url = new URL(route, window.location.href);
        persistedParamToRemove.forEach(param => url.searchParams.delete(param));
        return url.href;
    };

    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer,
            MobileMenuContainer,
            MobileMenuBodyContainer,
            MobileMenuLinksContainer,
            context,
            resources,
            data
        } = this.props;

        // Remove set width from logo image
        const logoProps = (this.props.logo as React.ReactElement).props;
        if (logoProps?.image?.width) {
            delete logoProps.image.width;
        }
        const logo = React.cloneElement(this.props.logo as React.ReactElement, logoProps);

        return (
            <>
                <Module {...HeaderTag}>
                    <Node {...HeaderContainer}>
                        <Node {...HeaderTopBarContainer}>
                            <Node className='ms-header__logo-container'>
                                {logo}
                                {this._renderMobileLogo()}
                            </Node>

                            {context.request.user.isAuthenticated && data.accountInformation.result && this.props.search && ArrayExtensions.hasElements(this.props.search) && (
                                <Node className={classnames(`${HeaderTopBarContainer.className}__search-container`, { active: this.state.searchActive })}>
                                    {this._renderReactFragment(this.props.search)}
                                </Node>
                            )}

                            <Node className={classnames(`${HeaderTopBarContainer.className}__right`, { 'slide-off': this.state.searchActive })}>
                                {!context.request.user.isAuthenticated && (
                                    this._renderAccountBlock(this.props)
                                )}

                                {context.request.user.isAuthenticated && data.accountInformation.result && (
                                    <>
                                    <a className={`${this.props.className}__link`} href={getUrlSync('ordertemplate', context.actionContext)}>
                                        <img src={getAsset('icons/list.svg', context.request)} />
                                        <span>{this.props.resources.headerOrderTemplateLabel}</span>
                                    </a>

                                    {this.props.cartIcon}

                                    <a className={`${this.props.className}__link`} href={getUrlSync('myaccount', context.actionContext)}>
                                        <img src={getAsset('icons/profile.svg', context.request)} />
                                        <span>{this.props.resources.headerProfileLabel}</span>
                                    </a>

                                    {this.props.isOBORequest && (
                                        <a
                                            aria-label={resources.switchCustomerLinkAriaText}
                                            className='ms-signin-info__account-link-button'
                                            title={resources.switchCustomerLinkText}
                                            href={this.state.customerSwitchUrl}
                                            role='link'
                                            onClick={(e) => {
                                                context.request.cookies.removeAccountSelectionCookie();

                                                if (!(e.target as HTMLElement).getAttribute('href')) {
                                                    // Refresh the page
                                                    window.location.reload();
                                                }
                                            }}
                                        >
                                            <div className='ms-header__switchcustomer-button-text'>
                                                <div>{resources.switchCustomerLinkText}</div>
                                            </div>
                                        </a>
                                    )}
                                    </>
                                )}

                                {context.request.user.isAuthenticated && (
                                    <>
                                    <a className={`${this.props.className}__link logout`} href={context.request.user.signOutUrl}>
                                        <img src={getAsset('icons/logout.svg', context.request)} />
                                        <span>{this.props.resources.logoutLabel}</span>
                                    </a>
                                    </>
                                )}

                                {this._renderReactFragment(this.props.siteOptions)}
                            </Node>

                            {/* {this.props.preferredStore}
                            {this.props.currentDistributor} */}
                        </Node>
                        <Node className='ms-header__navigation-bar'>
                            <div className='desktop-navicon-container' ref={this.menuNode}>
                                {this._renderReactFragment(this.props.menuBar)}
                            </div>
                        </Node>
                        <Node {...MobileMenuContainer} data-m-t={this.props.typeName}>
                            <Node {...MobileMenuBodyContainer}>
                                {this.props.MobileMenuHeader}
                                {this._renderReactFragment(this.props.menuBar)}
                                <Node {...MobileMenuLinksContainer}>
                                    {this.props.accountLinks ? this.props.accountLinks.map(link => link) : false}
                                    {this.props.siteOptions}
                                    {context.request.user.isAuthenticated && this.props.wishListIconMobile}
                                    {this.props.signInLink}
                                    {this.props.signOutLink}
                                </Node>
                            </Node>
                        </Node>
                    </Node>
                </Module>
            </>
        );
    }

    private readonly _monitorHeaderHeight = (): void => {
        if (!MsDyn365.isBrowser) {
            return;
        }

        // Set initial header height
        const root_theme = document.querySelector(':root') as HTMLElement;
        const headerHeight = document.querySelector('#renderPage > header')?.clientHeight || 0;
        root_theme.style.setProperty('--msv-actual-header-height', `${headerHeight}px`);
        this._previousHeaderHeight = headerHeight;

        // Create observer to monitor header height
        const observer = new (window as any).ResizeObserver(() => {
            const header = document.querySelector('#renderPage > header');
            const headerNewHeight = header?.clientHeight || 0;

            if (headerNewHeight !== this._previousHeaderHeight) {
                this._previousHeaderHeight = headerNewHeight;
                root_theme.style.setProperty('--msv-actual-header-height', `${headerNewHeight}px`);
            }
        });

        observer.observe(document.querySelector('#renderPage > header') as HTMLElement);
    };

    private _renderMobileLogo(): JSX.Element | null {
        const { config, context, typeName } = this.props;
        const mobileLogoImage = config.mobileLogoImage ?? {};

        if (mobileLogoImage.width) {
            delete mobileLogoImage.width;
        }

        return (
            <Logo
                {...{
                    link: config.logoLink,
                    image: mobileLogoImage,
                    className: 'ms-header__mobile-logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName
                }}
            />
        );
    }

    private readonly _hasNotFoundError = () => {
        const { context: { request: { user } } } = this.props;
        const notFoundError = 'Microsoft_Dynamics_Commerce_Runtime_CustomerNotFound_WhenAutoLinking';
        const multipleAccountError = 'Microsoft_Dynamics_Commerce_Runtime_MultipleCustomerAccountsFoundWithSameEmailAddress';

        return user?.retailServerErrorCode === notFoundError || user?.retailServerErrorCode === multipleAccountError;
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderAccountBlock = (props: IHeaderViewProps): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            AccountInfoDropdownParentContainer,
            AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signInLink
        } = props;
        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>{this._renderAccountDropdownLinks(props)}</Node>
                    </Node>
                );
            } else if (signInLink) {
                // Add tabindex to button/link to make it focusable on iOS, otherwise it breaks :focus-within selector
                const fixedSignInLink = React.cloneElement(signInLink as React.ReactElement, { tabindex: 0 });
                const hideLoginButton = this.props.config.hideLoginButton;

                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {!this._hasNotFoundError() ?
                                <div className='ms-header__account-info__dropdown' ref={this._accountInfoDropdownRef}>
                                    <button
                                        className={classnames('ms-header__account-info__dropdown__button', { open: this.state.isAccountMobileMenuOpen })}
                                        tabIndex={0}
                                        onClick={() => this.setState({ isAccountMobileMenuOpen: !this.state.isAccountMobileMenuOpen })}
                                    >
                                        <span className='ms-header__account-info__dropdown__button__label'></span>

                                        <div className='ms-header__account-info__dropdown__content'>
                                            {this._renderSupplierPortal()}
                                            {!hideLoginButton ? fixedSignInLink : undefined}
                                            {this.props.config.LinkText ? (
                                                <a
                                                    id='vinhusetCreateAnAccount'
                                                    className='msc-btn ms-header__become-customer-button'
                                                    href={this.props.config.LinkUrl}
                                                    aria-label={this.props.config.LinkText}
                                                    tabIndex={0}
                                                >
                                                    {this.props.config.LinkText}
                                                </a>
                                            ) : undefined}
                                        </div>
                                    </button>
                                </div>
                            :
                                <a id='vinhusetLogout' href={this.props.config.logoutUrl} className='msc-btn' aria-label={this.props.resources.logoutLabel ? this.props.resources.logoutLabel : "Logout"}>
                                    {this.props.resources.logoutLabel ? this.props.resources.logoutLabel : "Logout"}
                                </a>
                            }
                    </Node>
                );
            }
        }
        return null;
    };

    private readonly _renderAccountDropdownLinks = (props: IHeaderViewProps): JSX.Element | null => {
        const { isOBORequest, switchCustomerLink, accountLinks, signOutLink } = props;
        if (isOBORequest) {
            const displayName = props.data.employee.result?.Name;
            const customerName = props.data.accountInformation.result?.Name;

            return (
                <>
                    {customerName && (
                        <div className='ms-header__account-info-content__welcome'>
                            <div className='ms-header__account-info-content__welcome__message'>{displayName}</div>
                            <div className='ms-header__account-info-content__welcome__name'>{format(props.resources.shoppingAsText, customerName)}</div>
                        </div>
                    )}
                    {switchCustomerLink}
                    {signOutLink}
                    {accountLinks ? <Node className='ms-profile-button-splitter' /> : false}
                    {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                </>
            );
        } else {
            return (
                <>
                    <div className='ms-header__account-info-content__welcome'>
                        <div className='ms-header__account-info-content__welcome__message'>{this.props.resources.welcomeNameAuthenticated}</div>
                        <div className='ms-header__account-info-content__welcome__name'>{props.context.request.user.name}</div>
                    </div>
                    {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                    {signOutLink}
                </>
            );
        }
    };

    private readonly _renderSupplierPortal = (): JSX.Element | null => {
        const {
            config: {
                supplierPortalLinkLabel,
                supplierPortalLink
            }
        } = this.props;

        if (!supplierPortalLink || !supplierPortalLink.destinationUrl) {
            return null;
        }

        return (
            <a className='ms-header__supplier-portal-link' href={supplierPortalLink.destinationUrl}>
                {supplierPortalLinkLabel}
            </a>
        );
    };

    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items)
                    ? items.map((slot: React.ReactNode) => {
                          return <>{slot}</>;
                      })
                    : null}
            </>
        );
    };
}

export default HeaderView;
